import React, { useEffect, useRef, useState } from "react";
import { fetchQuotes, matchQuote } from "./api";
import { trackQuoteMatch } from "../../utils/analytics";
import { Deck } from "./deck";
import QuoteCard from "./QuoteCard";
import HeroLink from "./HeroLink";
import RoryCourses from "./RoryCourses";
import SignupBox from "./SignupBox";
import Sources from "./Sources";
import { RoryQuote } from "./types";

const OPENING_NUMBER = 1;

const Finder = () => {
  const [quotes, setQuotes] = useState<RoryQuote[] | null>(null);
  const [current, setCurrent] = useState<{ quote: RoryQuote; reason?: string } | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [brief, setBrief] = useState("");
  const [finding, setFinding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deck = useRef<Deck | null>(null);

  useEffect(() => {
    fetchQuotes()
      .then((all) => {
        const opening = all.find((q) => q.number === OPENING_NUMBER) || all[0];
        deck.current = new Deck(all, opening?.id);
        setQuotes(all);
        if (opening) setCurrent({ quote: opening });
      })
      .catch(() => setError("The quotes could not be loaded. Please try again in a moment."));
  }, []);

  const showAnother = () => {
    if (!deck.current) return;
    setError(null);
    setCurrent({ quote: deck.current.next(current?.quote.id) });
  };

  const findQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = brief.trim();
    if (text.length < 2) {
      setError("Give us a sentence to work with.");
      return;
    }
    setError(null);
    setFinding(true);
    try {
      const result = await matchQuote(text);
      trackQuoteMatch({ tool: "rory" });
      deck.current?.markSeen(result.quote.id);
      setCurrent({ quote: result.quote, reason: result.reason });
      setSearchOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setFinding(false);
    }
  };

  return (
    <div className="rq">
      <section className="rq-hero">
        <div className="rq-wrap rq-hero__inner">
          <header className="rq-hero__head">
            <span className="rq-hero__avatar">
              <img src="/rory-quotes/rory-sketch.png" alt="A hand-drawn ink sketch of Rory Sutherland" width="112" height="112" />
            </span>
            <div className="rq-hero__copy">
              <h1 className="rq-hero__title">
                Rory Quote <em>Finder</em>
              </h1>
              <p className="rq-hero__sub">
                {quotes ? quotes.length : "200+"} verified Rory Sutherland quotes. Find the right one in seconds, or get a quote and a behavioural bias by email every Monday.
              </p>
            </div>
            <HeroLink to="/rory-quotes/top">Top 10 quotes</HeroLink>
          </header>

          <div className="rq-finder">
            <div aria-live="polite" aria-busy={finding}>
              {current ? (
                <QuoteCard key={current.quote.id} quote={current.quote} reason={current.reason} />
              ) : (
                <div className="rq-card rq-card--loading">{error || "Finding a good one…"}</div>
              )}
            </div>
            <div className="rq-finder__acts">
              <button type="button" className="rq-btn rq-btn--blue" onClick={showAnother} disabled={!quotes || quotes.length < 2 || finding}>
                Show me another
              </button>
              <button
                type="button"
                className="rq-finder__toggle"
                onClick={() => setSearchOpen(!searchOpen)}
                aria-expanded={searchOpen}
                aria-controls="rq-topic-search"
              >
                {searchOpen ? "Close topic search" : "Find a quote for a topic"}
              </button>
            </div>
            {searchOpen && (
              <form id="rq-topic-search" className="rq-finder__form" onSubmit={findQuote}>
                <label htmlFor="rq-brief">What do you need a quote for?</label>
                <textarea
                  id="rq-brief"
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="e.g. Opening a talk on creativity"
                />
                <button type="submit" className="rq-btn rq-btn--blue" disabled={finding}>
                  {finding ? "Finding your quote…" : "Find a quote"}
                </button>
              </form>
            )}
            {error && current && (
              <p role="alert" className="rq-finder__error">
                {error}
              </p>
            )}
          </div>
        </div>
      </section>
      <SignupBox />
      <RoryCourses />
      <Sources />
    </div>
  );
};

export default Finder;
